import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useContestGroup } from '@/hooks/useContestGroup';
import { useCreateContestTemplate } from '@/hooks/useContestTemplate';
import {
  CreateContestFormValues,
  createContestSchema,
} from '@/modules/fantasy/create-contest/schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { Outlet, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'sonner';

export const Route = createFileRoute(
  '/dashboard/fantasy/contest-template/add-template'
)({
  component: AddTemplateLayout,
});

function AddTemplateLayout() {
  const [showDialog, setShowDialog] = useState(false);
  const navigate = useNavigate();
  const { isLoading, data: contestGroupData } = useContestGroup();

  const isAuthorized = true;
  const contestGroupsMap = useMemo(() => {
    if (!isLoading && contestGroupData) {
      return contestGroupData.data.groups.reduce(
        (
          acc: Record<string, string>,
          group: { groupId: number; name: string }
        ) => {
          acc[group.groupId.toString()] = group.name;
          return acc;
        },
        {}
      );
    } else {
      return {};
    }
  }, [contestGroupData, isLoading]);

  const methods = useForm<CreateContestFormValues>({
    resolver: zodResolver(createContestSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      minParticipants: 2,
      prizeDistributionType: 'FIXED_VALUE',
      rewardMatrix: [
        {
          amount: 0,
          prizeType: 'GT',
          rankStart: 1,
          rankEnd: 1,
        },
      ],
    },
  });

  const { mutateAsync, isPending } = useCreateContestTemplate();

  const onSubmit = async (values: CreateContestFormValues) => {
    try {
      if (!isPending) {
        await mutateAsync(values);
        toast.success('Contest added successfully', {
          className: 'bg-black text-white border-0',
          position: 'bottom-right',
          duration: 3000,
        });
      }
    } catch (error) {
      console.error(error);
      toast.error('Something went wrong', {
        className: 'bg-black text-white border-0',
        position: 'bottom-right',
        duration: 3000,
      });
    }
  };

  const handleProceed = async () => {
    const isValid = await methods.trigger();
    if (isValid) {
      setShowDialog(true);
    } else {
      toast.error('Please fill all required fields correctly', {
        className: 'bg-black text-white border-0',
        position: 'bottom-right',
        duration: 3000,
      });
    }
  };

  if (!isAuthorized) {
    return (
      <div className='p-6 px-20 flex w-full min-h-screen items-center justify-center'>
        <Card className='flex flex-1 max-w-md'>
          <CardHeader>
            <CardTitle className='text-center text-red-600'>
              Access Restricted
            </CardTitle>
          </CardHeader>
          <CardContent className='text-center space-y-4'>
            <p className='text-gray-600'>
              You don&apos;t have permission to create contest templates.
            </p>
            <p className='text-sm text-gray-500'>
              Please contact <strong>Manas</strong> at{' '}
              <a
                href='mailto:manas@joinjumbo.com'
                className='text-blue-600 hover:underline'
              >
                manas@joinjumbo.com
              </a>{' '}
              for access to this feature.
            </p>
            <Button onClick={() => navigate({ to: '..' })} className='w-full'>
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <div className='p-6 px-20 flex w-full min-h-screen items-start justify-center overflow-y-auto'>
        <Card className='flex flex-1'>
          <CardHeader>
            <CardTitle>Create Contest Template</CardTitle>
          </CardHeader>
          <CardContent>
            <Outlet />
            <Separator className='my-6' />
            <div className='flex justify-end gap-4'>
              <Button
                type='button'
                variant='destructive'
                className='w-24'
                onClick={() => navigate({ to: '..' })}
              >
                Cancel
              </Button>
              <Button
                type='button'
                className='w-24'
                onClick={handleProceed}
                disabled={!methods.formState.isValid}
              >
                Proceed
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={showDialog} onOpenChange={setShowDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              <Card>
                <CardContent className='space-y-2 h-[50vh] overflow-scroll'>
                  <h2 className='text-lg font-base'>Contest Details</h2>

                  <div className='w-full flex flex-row justify-between'>
                    <strong>Name:</strong> {methods.watch('name') || ''}
                  </div>
                  <div className='w-full flex flex-row justify-between'>
                    <strong>Category:</strong>
                    <Badge>
                      {contestGroupsMap[methods.watch('contestGroupId')] || ''}
                    </Badge>
                  </div>
                  <div className='w-full flex flex-row justify-between'>
                    <strong>Type:</strong> {methods.watch('contestType') || ''}
                  </div>
                  <div className='w-full flex flex-row justify-between'>
                    <strong>Entry Fee:</strong>
                    {methods.watch('entryFee') ?? ''}
                  </div>

                  <div className='w-full flex flex-row justify-between'>
                    <strong>Min Players:</strong>{' '}
                    {methods.watch('minParticipants') ?? ''}
                  </div>
                  <div className='w-full flex flex-row justify-between'>
                    <strong>Max Players:</strong>{' '}
                    {methods.watch('maxParticipants') ?? ''}
                  </div>
                  <div className='w-full flex flex-row justify-between'>
                    <strong>Max Teams/User:</strong>{' '}
                    {methods.watch('maxEntries') ?? ''}
                  </div>

                  <div className='w-full flex flex-row justify-between'>
                    <strong>Mutli Join:</strong>{' '}
                    {methods.watch('isMultijoin') ? '✅' : '❌'}
                  </div>
                  <div className='w-full flex flex-row justify-between'>
                    <strong>Auto Recurring:</strong>{' '}
                    {methods.watch('autoRecurring') ? '✅' : '❌'}
                  </div>
                  <div className='w-full flex flex-row justify-between'>
                    <strong>Auto Publish:</strong>{' '}
                    {methods.watch('autoPublish') ? '✅' : '❌'}
                  </div>
                  <div className='w-full flex flex-row justify-between'>
                    <strong>Priority :</strong>{' '}
                    <Badge>{methods.watch('priority')}</Badge>
                  </div>
                  <Separator />
                  <strong>Prize Distribution:</strong>
                  <Card className='mt-5'>
                    <CardContent className='space-y-2'>
                      <div className='flex font-base text-sm text-muted-foreground justify-between px-2'>
                        <span>From</span>
                        <span>To</span>
                        <span>Amt</span>
                        <span>Type</span>
                        <span>Total</span>
                      </div>
                      <Separator />

                      {methods.watch('rewardMatrix')?.map((item, index) => (
                        <div key={index}>
                          <div className='flex flex-row justify-between items-center gap-4 px-2'>
                            <Badge variant='outline'> {item.rankStart} </Badge>
                            <Badge variant='outline'> {item.rankEnd} </Badge>
                            <span className='text-sm'>{item.amount}</span>
                            <span className='text-sm'>{item.prizeType}</span>
                            <span className='font-medium text-sm'>
                              {item.amount *
                                (item.rankEnd - item.rankStart + 1)}
                            </span>
                          </div>
                          {index < methods.watch('rewardMatrix').length - 1 && (
                            <Separator className='my-2' />
                          )}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </CardContent>
              </Card>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={e => {
                e.preventDefault();
                methods.handleSubmit(onSubmit)();
              }}
            >
              Add Contest Template
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FormProvider>
  );
}
